import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";

import { Box, Typography, Paper, Grid, TextField, Button } from "@mui/material";
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const CandidateFinancialDetails = () => {
  const navigate = useNavigate();
  const location = useLocation();
  
  const [employeeId, setEmployeeId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [companyCountry, setCompanyCountry] = useState("India");
  const [bankCodeLabel, setBankCodeLabel] = useState("IFSC Code");
  
  const [formData, setFormData] = useState({
    employeeDetails: {
      employeeId: "",
      department: "",
      costCenter: "",
      resignationDate: "",
      noticePeriod: "",
      advanceSalary: "",
      creditCardOffered: "",
    },
    finance: {
      bankName: "",
      accountHolderName: "",
      accountDetails: "",
      accountType: "",
      branch: "",
      ifscCode: "",
    },
    perviousfinance: {
      currentSalary: "",
      previousSalary: "",
      ctc: "",
      taxCalculation: "",
    }
  });

  const [errors, setErrors] = useState({
    finance: {
      bankName: false,
      accountDetails: false,
      accountDetailsMessage: "",
      ifscCode: false,
      ifscCodeMessage: ""
    }
  });

  // Fetch current user to get employee ID
  useEffect(() => {
    const fetchCurrentUser = async () => {
      try {
        const token = localStorage.getItem("token");
        if (!token) {
          alert("No authentication token found. Please log in.");
          setLoading(false);
          return;
        }

        const response = await axios.get(`${API_URL}/api/user/current`, {
          withCredentials: true
        });
        
        if (response.data.employee_id) {
          setEmployeeId(response.data.employee_id);
          // Fetch financial data using the employee_id
          await fetchFinancialData(response.data.employee_id);
        } else {
          //console.error("No employee ID found in user data");
          setLoading(false);
        }
      } catch (err) {
        //console.error("Error fetching current user:", err);
        setLoading(false);
      }
    };
  
    fetchCurrentUser();
  }, []);

  const fetchFinancialData = async (empId) => {
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        alert("No authentication token found. Please log in.");
        return;
      }

      const payload = JSON.parse(atob(token.split('.')[1]));
      const company_id = payload.company_id;
      
      if (company_id) {
        try {
          const companyResponse = await axios.get(`${API_URL}/api/cmp/${company_id}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          if (companyResponse.data.success && companyResponse.data.data?.country) {
            setCompanyCountry(companyResponse.data.data.country);
            if (companyResponse.data.data.bankCodeLabel) setBankCodeLabel(companyResponse.data.data.bankCodeLabel);
          }
        } catch (cErr) {
          console.error("Error fetching company country:", cErr);
        }
      }

      const response = await axios.get(
        `${API_URL}/api/financial/${empId}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      if (response.data.success && response.data.data) {
        const financialData = response.data.data; 
        setFormData({
          employeeDetails: {
            employeeId: empId,
            department: financialData.department || "",
            costCenter: financialData.costCenter ? `${financialData.costCenter.name} (${financialData.costCenter.code})` : "Not Assigned",
            resignationDate: financialData.resignationDate || "",
            noticePeriod: financialData.noticePeriod || "",
            advanceSalary: financialData.advanceSalary || "",
            creditCardOffered: financialData.creditCardOffered || "",
          },
          finance: {
            bankName: financialData.bankName || "",
            accountHolderName: financialData.accountHolderName || "",
            accountDetails: financialData.accountNumber || "", 
            accountType: financialData.accountType || "",
            branch: financialData.branch || "",
            ifscCode: financialData.ifscCode || "",
          },
          perviousfinance: {
            currentSalary: financialData.currentSalary || "",
            previousSalary: financialData.previousSalary || "",
            ctc: financialData.ctc || "",
            taxCalculation: financialData.taxCalculation || "",
          }
        });
      } else {
        // If no financial data exists yet, still set the employee ID
        setFormData(prev => ({
          ...prev,
          employeeDetails: {
            ...prev.employeeDetails,
            employeeId: empId
          }
        }));
      }
    } catch (error) {
      //console.error("Error fetching financial data:", error);
      // If financial data doesn't exist yet, this is normal
      if (error.response?.status !== 404) {
        alert("Failed to load financial details");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e, section, field) => {
    const value = e.target.value;
    
    setFormData(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value
      }
    }));
  
    // Validate fields
    if (section === 'finance') {
      if (field === 'bankName') {
        setErrors(prev => ({
          ...prev,
          finance: {
            ...prev.finance,
            [field]: !value.trim()
          }
        }));
      } else if (field === 'accountDetails') {
        const validation = validateAccountNumber(value);
        setErrors(prev => ({
          ...prev,
          finance: {
            ...prev.finance,
            accountDetails: !validation.isValid,
            accountDetailsMessage: validation.message
          }
        }));
      } else if (field === 'ifscCode') {
        const validation = validateIFSC(value);
        setErrors(prev => ({
          ...prev,
          finance: {
            ...prev.finance,
            ifscCode: !validation.isValid,
            ifscCodeMessage: validation.message
          }
        }));
      }
    }
  };

  const validateAccountNumber = (accountNum) => {
    const accountNumber = accountNum.trim();
    if (!accountNumber) {
      return { isValid: false, message: "Account Number is required" };
    }
    
    // Check if account number only contains digits and is between 9-18 digits for India
    if (!companyCountry || companyCountry.toLowerCase() === 'india') {
      const accountRegex = /^\d{9,18}$/;
      if (!accountRegex.test(accountNumber)) {
        return { isValid: false, message: "Account Number must be 9-18 digits" };
      }
    } else {
      // For other countries, just ensure it's not purely whitespace and has a reasonable length
      if (accountNumber.length < 5) {
        return { isValid: false, message: "Account Number is too short" };
      }
    }
    
    return { isValid: true, message: "" };
  };
  
  const validateIFSC = (ifsc) => {
    const ifscCode = ifsc.trim();
    if (!ifscCode) {
      return { isValid: false, message: `${bankCodeLabel} is required` };
    }
    
    if (!companyCountry || companyCountry.toLowerCase() === 'india') {
      const ifscRegex = /^[A-Z]{4}0[A-Z0-9]{6}$/;
      if (!ifscRegex.test(ifscCode)) {
        return { isValid: false, message: "Invalid IFSC format. Should be like 'ABCD0123456'" };
      }
    }
    
    return { isValid: true, message: "" };
  };
  
  const validateForm = () => {
    const accountValidation = validateAccountNumber(formData.finance.accountDetails);
    const ifscValidation = validateIFSC(formData.finance.ifscCode);
    
    const newErrors = {
      finance: {
        bankName: !formData.finance.bankName.trim(),
        accountDetails: !accountValidation.isValid,
        accountDetailsMessage: accountValidation.message,
        ifscCode: !ifscValidation.isValid,
        ifscCodeMessage: ifscValidation.message
      }
    };
  
    setErrors(newErrors);
  
    return !(
      newErrors.finance.bankName || 
      newErrors.finance.accountDetails || 
      newErrors.finance.ifscCode ||
      !formData.employeeDetails.employeeId || 
      !formData.employeeDetails.department
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
  
    if (!validateForm()) {
      if (!formData.employeeDetails.employeeId || !formData.employeeDetails.department) {
        alert("Employee ID and Department are required!");
      } else {
        alert("Bank Name, Account Number, and IFSC Code are required!");
      }
      return;
    }
  
    const token = localStorage.getItem("token"); 
    if (!token) {
      alert("No authentication token found. Please log in.");
      return;
    }
  
    const payload = JSON.parse(atob(token.split('.')[1]));
    const company_id = payload.company_id;
    const userId = payload.id;

    try {
      const payloadData = {
        employee_id: formData.employeeDetails.employeeId,
        department: formData.employeeDetails.department,
        resignationDate: formData.employeeDetails.resignationDate || null,
        noticePeriod: formData.employeeDetails.noticePeriod,
        advanceSalary: formData.employeeDetails.advanceSalary,
        creditCardOffered: formData.employeeDetails.creditCardOffered,
        bankName: formData.finance.bankName,
        accountHolderName: formData.finance.accountHolderName,
        accountNumber: formData.finance.accountDetails, 
        accountType: formData.finance.accountType,
        branch: formData.finance.branch,
        ifscCode: formData.finance.ifscCode,
        currentSalary: parseFloat(formData.perviousfinance.currentSalary) || 0,
        previousSalary: parseFloat(formData.perviousfinance.previousSalary) || 0,
        ctc: parseFloat(formData.perviousfinance.ctc) || 0,
        taxCalculation: parseFloat(formData.perviousfinance.taxCalculation) || 0,
      };
  
      // Always use update endpoint since employees can only update their own details
      const response = await axios({
        method: "put",
        url: `${API_URL}/api/alldetails/candidatefinancial/${formData.employeeDetails.employeeId}`,
        data: payloadData,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
      });
  
      if (response.data.success || response.status === 200) {
        alert("Financial details updated successfully!");
        // Redirect to profile details page
        // navigate("/profile-details");
      }
    } catch (error) {
      //console.error("Submission error:", error);
      const errorMessage = error.response?.data?.message || 
        error.response?.data?.error || 
        "Failed to save financial details";
      alert(`Error: ${errorMessage}`);
    }
  };

  const customLabels = {
    accountHolderName: "Account Holder Name",
    accountDetails: "Account Number",
    accountType: "Account Type",
    branch: "Branch",
    ifscCode: bankCodeLabel,
    ctc: "CTC",
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <Typography variant="h6">Loading your financial details...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 1500, margin: "auto", padding: 3 }}>
      <Typography variant="h4" gutterBottom  fontWeight="bold">
        Add Financial Details
      </Typography>
      
      <Paper elevation={3} sx={{ padding: 3, marginBottom: 3 }}>
        <Typography variant="h6" gutterBottom> Employee Details </Typography>
        <Grid container spacing={2}>
          {Object.keys(formData.employeeDetails).map((field) => (
            <Grid item xs={6} key={field}>
              <TextField
                fullWidth
                label={field.replace(/([A-Z])/g, " $1").replace(/^./, str => str.toUpperCase()).trim()}
                type={field === "resignationDate" ? "date" : "text"}
                InputLabelProps={field === "resignationDate" ? { shrink: true } : {}}
                value={formData.employeeDetails[field]}
                onChange={(e) => handleChange(e, "employeeDetails", field)}
                required={["department", "employeeId"].includes(field)}
                disabled={field === "employeeId" || field === "costCenter"}
              />
            </Grid>
          ))}
        </Grid>
      </Paper>

      <Paper elevation={3} sx={{ padding: 3, marginBottom: 3 }}>
        <Typography variant="h6" gutterBottom> Finance Information </Typography>
        <Grid container spacing={2}>
          {Object.keys(formData.finance).map((field) => (
            <Grid item xs={6} key={field}>
              <TextField
                fullWidth
                label={
                  customLabels[field] ||
                  field
                    .replace(/([A-Z])/g, " $1")
                    .replace(/\b\w/g, char => char.toUpperCase())
                    .trim()
                }
                value={formData.finance[field]}
                onChange={(e) => handleChange(e, "finance", field)}
                required={["bankName", "accountDetails", "ifscCode"].includes(field)}
                error={errors.finance[field]}
                helperText={errors.finance[field] ? 
                  (field === 'accountDetails' ? errors.finance.accountDetailsMessage : 
                   field === 'ifscCode' ? errors.finance.ifscCodeMessage :
                   `${customLabels[field] || field.replace(/([A-Z])/g, " $1").replace(/\b\w/g, char => char.toUpperCase()).trim()} is required`) 
                  : ""}
              />
            </Grid>
          ))}
        </Grid>
      </Paper>

      <Paper elevation={3} sx={{ padding: 3, marginBottom: 3 }}>
        <Typography variant="h6" gutterBottom> Salary Information </Typography>
        <Grid container spacing={2}>
          {Object.keys(formData.perviousfinance).map((field) => (
            <Grid item xs={6} key={field}>
              <TextField
                fullWidth
                label={
                  customLabels[field] ||
                  field
                    .replace(/([A-Z])/g, " $1")
                    .replace(/\b\w/g, char => char.toUpperCase())
                    .trim()
                }
                value={formData.perviousfinance[field]}
                onChange={(e) => handleChange(e, "perviousfinance", field)}
              />
            </Grid>
          ))}
        </Grid>
      </Paper>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 4 }}>
        <Button 
          variant="contained" 
          color="primary" 
          onClick={handleSubmit}
          size="large"
        >
          Update Financial Details
        </Button>
      </Box>
    </Box>
  );
};

export default CandidateFinancialDetails;